import { config } from "@/shared/config";
import { auth } from "@/shared/lib/firebase";

/** Always returns a fresh Firebase ID token, or null if not signed in. */
export async function getFirebaseToken(): Promise<string | null> {
  const user = auth.currentUser;
  if (!user) return null;
  return user.getIdToken();
}

/** Build Authorization header from current Firebase token. */
export async function firebaseAuthHeaders(): Promise<HeadersInit> {
  const token = await getFirebaseToken();
  if (!token) return {};
  return { Authorization: `Bearer ${token}` };
}

export class ApiError extends Error {
  status: number;
  code?: string;
  details?: unknown;

  constructor(message: string, opts: { status: number; code?: string; details?: unknown }) {
    super(message);
    this.name = "ApiError";
    this.status = opts.status;
    this.code = opts.code;
    this.details = opts.details;
  }
}

type ApiFetchOptions = Omit<RequestInit, "body"> & {
  body?: unknown;
};

/** Optional: when provided, 401 responses trigger refresh then one retry with new token. */
export type AuthRetry = {
  getToken: () => string | null;
  refreshTokens: () => Promise<boolean>;
  /** Optional: ms-since-epoch when the current token expires. */
  getTokenExpiresAt?: () => number | null;
};

const API_BASE_URL = config.apiBaseUrl ?? "";

export type ApiResponse<T> = {
  success: boolean;
  message?: string;
  data?: T;
};

function joinUrl(base: string, path: string) {
  if (!base) return path;
  if (base.endsWith("/") && path.startsWith("/")) return base + path.slice(1);
  if (!base.endsWith("/") && !path.startsWith("/")) return `${base}/${path}`;
  return base + path;
}

async function safeParseJson(res: Response) {
  const contentType = res.headers.get("content-type") ?? "";
  if (!contentType.includes("application/json")) return null;
  try {
    return await res.json();
  } catch {
    return null;
  }
}

/**
 * Typed fetch wrapper with JSON serialization + consistent error handling.
 *
 * - In local dev, we proxy `/api` via Vite so `VITE_API_BASE_URL_DEV` can be empty.
 * - Uses `credentials: "include"` (safe even when using JWT in headers).
 * - If authRetry is provided and response is 401, refreshes token and retries once (no reload needed).
 */
export async function apiFetch<TResponse>(
  path: string,
  options: ApiFetchOptions = {},
  authRetry?: AuthRetry
): Promise<TResponse> {
  const url = joinUrl(API_BASE_URL, path);

  const baseHeaders = {
    "content-type": "application/json",
    ...(options.headers ?? {}),
  };
  const doRequest = (headers: HeadersInit) =>
    fetch(url, {
      ...options,
      credentials: options.credentials ?? "include",
      headers: { ...baseHeaders, ...headers },
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
    });

  // Proactively refresh if the token is already expired (avoids a guaranteed
  // 401 round-trip and keeps the backend logs clean).
  if (authRetry?.getTokenExpiresAt) {
    const expiresAt = authRetry.getTokenExpiresAt();
    if (expiresAt && expiresAt <= Date.now()) {
      await authRetry.refreshTokens();
    }
  }

  let res = await doRequest({});

  if (res.status === 401 && authRetry) {
    const ok = await authRetry.refreshTokens();
    if (ok) {
      const newToken = authRetry.getToken();
      if (newToken) {
        res = await doRequest({ ...(options.headers ?? {}), Authorization: `Bearer ${newToken}` });
      }
    }
  }

  if (!res.ok) {
    const data = await safeParseJson(res);
    const message =
      (data && typeof data === "object" && "message" in data && typeof (data as any).message === "string"
        ? (data as any).message
        : `Request failed (${res.status})`);

    throw new ApiError(message, {
      status: res.status,
      code: data && typeof data === "object" && "code" in data ? String((data as any).code) : undefined,
      details: data,
    });
  }

  // Support endpoints that return 204/empty body.
  if (res.status === 204) return undefined as TResponse;

  const data = await safeParseJson(res);
  return data as TResponse;
}

/**
 * Helper for backends that return `{ success, message, data }`.
 * Returns `data` and surfaces backend errors consistently.
 * Pass authRetry for authenticated calls so 401 triggers refresh and one retry (no page reload).
 */
export async function apiFetchData<TData>(
  path: string,
  options: ApiFetchOptions = {},
  authRetry?: AuthRetry
): Promise<TData> {
  const envelope = await apiFetch<ApiResponse<TData>>(path, options, authRetry);

  if (envelope && typeof envelope === "object" && "success" in envelope) {
    if (!envelope.success) {
      throw new ApiError(envelope.message ?? "Request failed", {
        status: 200,
        details: envelope,
      });
    }
    return envelope.data as TData;
  }

  // Fallback: not an envelope response.
  return envelope as unknown as TData;
}

export function withAuthHeader(headers: HeadersInit | undefined, token: string | null | undefined): HeadersInit | undefined {
  if (!token) return headers;
  return {
    ...(headers ?? {}),
    Authorization: `Bearer ${token}`,
  };
}


